import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import { Edit, ExpandMore, ExpandLess } from '@material-ui/icons'

import { strings } from '@constants'
import { TOAST_TYPE } from '@base'
import { showToast } from '@data/state/action'

import { BasicDetailsCard, CardHeader, CardTitle, HeaderRight, EditBtn, ChevronBtn } from './style'

import BasicDetailsForm, { DELIVERY_STATUS_OPTIONS } from './BasicDetailForm'
import { saveInvoice, buildBaseInvoicePayload, toVendorBillList } from './invoiceApi'
import { Button } from '@baseV2'

const UpdateBtn = styled(Button)`
	margin: 0;
`

const isValidUrl = (s) => {
	try {
		return Boolean(new URL(s))
	} catch {
		return false
	}
}

const buildForm = (d) => ({
	original_invoice_number: d?.original_invoice_number || d?.invoice_number || '',
	invoice_date: d?.invoice_date ?? null,
	invoice_amount: d?.invoice_amount ?? '',
	invoice_quantity: d?.invoice_quantity ?? '',
	eway_bill_no: d?.eway_details?.e_way_bill_no || '',
	eway_expiry: d?.eway_details?.valid_till ? new Date(d.eway_details.valid_till) : null,
	delivery_status: DELIVERY_STATUS_OPTIONS.find((o) => o.status === d?.delivery_status) ?? null,
	delivery_date: d?.delivery_date ?? null,
	gps_link: d?.gps_link || '',
	// The API stores vendor bill numbers as an array of strings; the form edits them as one
	// comma-separated field and converts back on save.
	vendor_bill_no: toVendorBillList(d?.vendor_bill_no).join(', '),
})

const BasicDetail = ({ invoiceData, order, supplierPoNumber, update, editable = false, deliveryOnly = false }) => {
	const dispatch = useDispatch()

	const [isOpen, setIsOpen] = useState(true)
	const [isEditMode, setIsEditMode] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [formState, setFormState] = useState({})
	const [errors, setErrors] = useState({})

	const initialFormRef = useRef(null)
	const cardRef = useRef(null)

	const setField = useCallback((key, value) => {
		setFormState((prev) => ({ ...prev, [key]: value }))
		setErrors((prev) => {
			const next = { ...prev, [key]: undefined }
			if (key === 'original_invoice_number' || key === 'invoice_amount') {
				next.original_invoice_number = undefined
				next.invoice_amount = undefined
			}
			return next
		})
	}, [])

	const exitEditMode = useCallback(() => {
		setIsEditMode(false)
		setErrors({})
		initialFormRef.current = null
	}, [])

	const enterEditMode = useCallback(() => {
		const initial = buildForm(invoiceData)
		initialFormRef.current = initial
		setFormState(initial)
		setErrors({})
		setIsEditMode(true)
	}, [invoiceData])

	const validate = useCallback(() => {
		const e = {}
		// Delivery-only editors can't change the other fields, so they must not block the save.
		if (deliveryOnly) {
			setErrors(e)
			return true
		}
		if (!formState.original_invoice_number?.trim()) e.original_invoice_number = 'Invoice number is Required'
		if (!formState.invoice_amount) e.invoice_amount = 'Invoice amount is required'
		else if (formState.invoice_amount && Number(formState.invoice_amount) <= 0) e.invoice_amount = 'Must be a positive number'
		if (!formState.invoice_date) e.invoice_date = 'Invoice date is required'
		// if (formState.invoice_quantity && Number(formState.invoice_quantity) <= 0) e.invoice_quantity = 'Must be a positive number'
		if (formState.gps_link && !isValidUrl(formState.gps_link)) e.gps_link = 'Enter a valid URL'
		setErrors(e)
		return Object.keys(e).length === 0
	}, [formState, deliveryOnly])

	const handleUpdate = useCallback(async () => {
		if (!validate()) return
		setIsSaving(true)
		const deliveryFields = {
			delivery_status: formState.delivery_status?.status ?? null,
			delivery_date: formState.delivery_date ?? null,
		}
		const payload = deliveryOnly
			? { ...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber), ...deliveryFields }
			: {
					...buildBaseInvoicePayload(invoiceData, order, supplierPoNumber),
					invoice_amount: Number(formState.invoice_amount),
					invoice_date: formState.invoice_date,
					original_invoice_number: formState.original_invoice_number,
					gps_link: formState.gps_link,
					...deliveryFields,
					vendor_bill_no: toVendorBillList(formState.vendor_bill_no),
			  }
		try {
			const ok = await saveInvoice({ payload, fallbackInvoice: invoiceData, order, invoiceData, dispatch })
			if (ok) {
				exitEditMode()
				update?.()
			}
		} catch (error) {
			// saveInvoice reports its own API failures; this only covers anything unexpected thrown
			// around it, so the user still gets feedback instead of a silent no-op.
			dispatch(showToast(true, error?.message ?? strings('error_something_went_wrong'), { type: TOAST_TYPE.ERROR }))
		} finally {
			setIsSaving(false)
		}
	}, [formState, invoiceData, order, dispatch, validate, exitEditMode, supplierPoNumber, update, deliveryOnly])

	useEffect(() => {
		if (!isEditMode) return
		setTimeout(() => {
			cardRef.current?.querySelector('input')?.focus()
		}, 50)
	}, [isEditMode])

	return (
		<BasicDetailsCard ref={cardRef}>
			<CardHeader>
				<CardTitle>Basic Details</CardTitle>
				<HeaderRight>
					{isEditMode ? (
						<>
							<Button
								variant='outline'
								type='danger'
								xs
								disabled={isSaving}
								onClick={() => {
									setIsEditMode(false)
								}}
							>
								{strings('cancel')}
							</Button>
							<UpdateBtn type='primary' xs disabled={isSaving} onClick={handleUpdate}>
								{isSaving ? 'Saving...' : strings('update')}
							</UpdateBtn>
						</>
					) : (
						editable && (
							<EditBtn onClick={enterEditMode}>
								<Edit fontSize='small' />
								{strings('edit')}
							</EditBtn>
						)
					)}
					<ChevronBtn onClick={() => setIsOpen((v) => !v)}>{isOpen ? <ExpandLess /> : <ExpandMore />}</ChevronBtn>
				</HeaderRight>
			</CardHeader>

			{isOpen && (
				<BasicDetailsForm
					invoiceData={invoiceData}
					supplierPoNumber={supplierPoNumber}
					formState={formState}
					setField={setField}
					errors={errors}
					isSaving={isSaving}
					isEditMode={isEditMode}
					deliveryOnly={deliveryOnly}
				/>
			)}
		</BasicDetailsCard>
	)
}

export default BasicDetail
